import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { deviceZone } from './time';
import { api, type AppCall, type ChatPresence, type ConversationKind, type MessageView, type PresenceList, type Reaction, type ReactionChange } from '../api/client';
import { announceHere } from './changes';

/**
 * Messages, live: one stream (server-sent events) per tab for the signed-in person, shared by the
 * menu's unread count and the Messages page. The browser reconnects by itself and Stitch replays
 * what it missed; when that's no longer known, a "reset" event says to load everything afresh.
 */

export interface LiveEvent {
  type: 'message' | 'message.updated' | 'read' | 'conversation' | 'patch' | 'presence' | 'alert' | 'line' | 'call' | 'voicemail' | 'picture' | 'changed' | 'calls' | 'offer' | 'meeting' | 'reset' | 'hello';
  /** meeting: a coworker asks this person to join a meeting going on now (a ring, until then). */
  meeting?: { id: string; title: string; path: string; by: { id: string; name: string }; until: string };
  /** offer: an ask by message changed (the Call for you card, the console). */
  offer?: import('../api/client').OfferView;
  tenant?: string;
  conversationId?: string;
  kind?: ConversationKind;
  message?: MessageView;
  lastReadSeq?: number;
  /** read: the person's new unread total in that company; in direct messages; and mentions. */
  unread?: number;
  dms?: number;
  mentions?: number;
  /** patch: who's in a Patch (a quick call from the conversation), or null when it ended. */
  patch?: unknown;
  /** patch: which one. ring: it's ringing this person (a direct message's), until then. started: one began in a channel (no ring). declined: someone said no to it ringing them. */
  patchId?: string;
  ring?: { by: { id: string; name: string }; until: string };
  started?: { by: { id: string; name: string }; title: string; until: string };
  declined?: { id: string; name: string };
  /** message.updated from a reaction: the change itself (applied to what's on screen, so counts stay right). */
  reaction?: ReactionChange;
  /** presence: who, and how they show now (on a call too). To the person alone: their own choice, made for them (do not disturb switched on their phone). */
  userId?: string;
  presence?: ChatPresence;
  me?: PresenceList['me'];
  /** alert: a message this person would be told about, and what its notification says (lib/alerts). voicemail: the message's id. */
  messageId?: string;
  alert?: { kind: 'message' | 'mention' | 'voicemail' | 'call'; title: string; body: string; url: string; tag: string };
  /** alert: a text for a ring group or queue (1.12.33): its own sound, "New text for Billing", and whether they're on a call now (one soft tone). */
  team?: { extension: string; name: string; urgent: boolean; onCall: boolean };
  /** line: an extension's line on the phone system changed (do not disturb, ringing, on a call, idle). */
  extension?: string;
  state?: 'idle' | 'ringing' | 'talking';
  dnd?: boolean;
  since?: string;
  /** call: a call on this person's own line in the app rang, connected, was held or ended (lib/calls). */
  call?: AppCall;
  /** voicemail: a new message in this person's mailbox (lib/voicemail): who left it, how long (seconds), when. */
  mailbox?: string;
  caller?: string;
  from?: { name: string; number: string };
  duration?: number;
  at?: string;
  /** picture: someone's new profile picture (its version), or null when removed (lib/pictures). */
  version?: string | null;
  /** voicemail: a message changed in Stitch (played, a note, moved, deleted) or arrived from another box. */
  change?: 'heard' | 'note' | 'moved' | 'deleted' | 'arrived';
  /** calls: a call ended (its id); missed: nobody answered a call to this person (lib/missed). */
  callId?: string;
  missed?: { extension: string; caller: string; number: string; at: string };
}

type Handler = (e: LiveEvent) => void;

const TYPES: LiveEvent['type'][] = ['message', 'message.updated', 'read', 'conversation', 'patch', 'presence', 'alert', 'line', 'call', 'voicemail', 'picture', 'changed', 'calls', 'offer', 'reset', 'hello'];
const handlers = new Set<Handler>();
/** Read events heard, per company: a total loaded before the latest one is out of date. */
const reads = new Map<string, number>();
let source: EventSource | null = null;
let lastId = '';
let users = 0;
let retry: number | undefined;

/** Whether the live stream is up: 'reconnecting' while it's down (Stitch says so after a few seconds). */
export type LiveLink = 'connected' | 'reconnecting';
let link: LiveLink = 'connected';
const linkListeners = new Set<() => void>();
function setLink(next: LiveLink): void {
  if (link === next) return;
  link = next;
  for (const l of [...linkListeners]) l();
}

/** The live stream's state, for the "Reconnecting…" note. */
export function useLiveLink(): LiveLink {
  return useSyncExternalStore(
    (l) => {
      linkListeners.add(l);
      return () => linkListeners.delete(l);
    },
    () => link,
    () => link
  );
}

/** When the page last went into the background (0: it's in front). */
let hiddenSince = 0;
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenSince = Date.now();
      return;
    }
    const away = hiddenSince ? Date.now() - hiddenSince : 0;
    hiddenSince = 0;
    // Back in front after a while (a phone that slept, a closed laptop): the stream is opened
    // afresh at once, picking up where it left off, rather than trusting a connection that may
    // have died quietly meanwhile.
    if (users > 0 && (!source || source.readyState === EventSource.CLOSED || away > 30_000)) {
      source?.close();
      source = null;
      window.clearTimeout(retry);
      connect();
    }
  });
}

function connect(): void {
  if (source || typeof EventSource === 'undefined') return;
  const es = new EventSource(`/api/messages/stream${lastId ? `?since=${encodeURIComponent(lastId)}` : ''}`);
  source = es;
  es.onopen = () => source === es && setLink('connected');
  for (const type of TYPES) {
    es.addEventListener(type, (ev) => {
      const m = ev as MessageEvent<string>;
      if (m.lastEventId) lastId = m.lastEventId;
      if (source === es) setLink('connected');
      let data: Partial<LiveEvent> = {};
      try {
        data = JSON.parse(m.data) as Partial<LiveEvent>;
      } catch {
        // Nothing to pass on.
      }
      if (type === 'read' && data.tenant) reads.set(data.tenant, (reads.get(data.tenant) ?? 0) + 1);
      for (const h of [...handlers]) h({ ...data, type });
    });
  }
  es.onerror = () => {
    if (source === es) setLink('reconnecting');
    // A dropped connection is retried by the browser; one that's refused (signed out) closes. Then
    // try again in a while, and load afresh once back, in case anything was missed.
    if (es.readyState !== EventSource.CLOSED) return;
    if (source === es) source = null;
    window.clearTimeout(retry);
    retry = window.setTimeout(() => {
      if (users > 0) {
        connect();
        for (const h of [...handlers]) h({ type: 'reset' });
      }
    }, 20_000);
  };
}

function disconnect(): void {
  source?.close();
  source = null;
  setLink('connected');
  window.clearTimeout(retry);
  stopBeats();
}

// --- "I'm here": tells Stitch the person is at it, so their phone isn't buzzed as well -----------------

const BEAT_MS = 30_000;
const INPUTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'mousemove'] as const;
let lastBeat = 0;
let beating = false;

function beat(): void {
  if (document.visibilityState !== 'visible' || Date.now() - lastBeat < BEAT_MS) return;
  lastBeat = Date.now();
  // With this device's time zone: coworkers see the zone of the device you used last (1.12.13).
  void api.messagesActivity(true, deviceZone()).catch(() => undefined);
}
function onVisibility(): void {
  if (document.visibilityState === 'visible') return;
  lastBeat = 0;
  // Gone to the background: the phone takes over at once.
  void fetch('/api/messages/activity', {
    method: 'POST',
    keepalive: true,
    headers: { 'content-type': 'application/json', 'x-stitch-request': '1' },
    body: JSON.stringify({ visible: false })
  }).catch(() => undefined);
}
function startBeats(): void {
  if (beating || typeof window === 'undefined') return;
  beating = true;
  // Opening Stitch counts as doing something: online straight away.
  beat();
  for (const e of INPUTS) window.addEventListener(e, beat, { passive: true });
  document.addEventListener('visibilitychange', onVisibility);
}
function stopBeats(): void {
  if (!beating) return;
  beating = false;
  for (const e of INPUTS) window.removeEventListener(e, beat);
  document.removeEventListener('visibilitychange', onVisibility);
}

/** Hears Messages' live events while mounted (and `on`). */
export function useMessagesLive(handler: Handler, on = true): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!on) return;
    const h: Handler = (e) => ref.current(e);
    handlers.add(h);
    users += 1;
    connect();
    startBeats();
    return () => {
      handlers.delete(h);
      users -= 1;
      if (users <= 0) {
        users = 0;
        disconnect();
      }
    };
  }, [on]);
}

/**
 * Something at this company was changed through Stitch, here or anywhere else (another device of
 * yours, a colleague): every screen showing it loads again, as after a change made in this tab.
 * So does losing track of the stream (what was missed isn't known).
 */
export function useCompanyChanges(tenant: string, on: boolean): void {
  const t = tenant.toLowerCase();
  useMessagesLive((e) => {
    if (e.type === 'reset' || (e.type === 'changed' && e.tenant?.toLowerCase() === t)) announceHere({ tenant: t });
  }, on && Boolean(t));
}

// --- presence: who's online, idle, busy or away, per company -------------------------------------------

const PRESENCE_MS = 30_000;
const presenceLists = new Map<string, { list: PresenceList | undefined; watchers: Set<() => void>; timer?: number; loading?: boolean }>();

function presenceEntry(tenant: string) {
  let e = presenceLists.get(tenant);
  if (!e) presenceLists.set(tenant, (e = { list: undefined, watchers: new Set() }));
  return e;
}

/** Loads the company's presence again (and on a timer while anyone looks: "on a call" comes from the phone system). */
export function reloadPresence(tenant: string): void {
  const e = presenceEntry(tenant);
  window.clearTimeout(e.timer);
  if (!e.watchers.size) return;
  if (!e.loading) {
    e.loading = true;
    api
      .messagePresence(tenant)
      .then((list) => {
        e.list = list;
        for (const w of [...e.watchers]) w();
      })
      .catch(() => undefined)
      .finally(() => (e.loading = false));
  }
  e.timer = window.setTimeout(() => reloadPresence(tenant), document.visibilityState === 'visible' ? PRESENCE_MS : PRESENCE_MS * 4);
}

/** Everyone in the company: online, idle, do not disturb or offline, their own status, on a call. */
export function usePresenceList(tenant: string): PresenceList | undefined {
  const key = tenant.toLowerCase();
  const [list, setList] = useState<PresenceList | undefined>(() => presenceLists.get(key)?.list);
  useEffect(() => {
    const e = presenceEntry(key);
    const follow = () => setList(e.list);
    e.watchers.add(follow);
    if (e.watchers.size === 1) reloadPresence(key);
    else follow();
    return () => {
      e.watchers.delete(follow);
      if (!e.watchers.size) window.clearTimeout(e.timer);
    };
  }, [key]);
  useMessagesLive((ev) => {
    if (ev.type === 'reset') return reloadPresence(key);
    if (ev.type !== 'presence' || ev.tenant !== key || !ev.userId || (!ev.presence && !ev.me)) return;
    const e = presenceEntry(key);
    if (!e.list) return;
    // How they show, on a call or not, wherever the call is (Stitch works it out from them all).
    if (ev.presence) e.list = { ...e.list, people: { ...e.list.people, [ev.userId]: ev.presence } };
    if (ev.me) e.list = { ...e.list, me: ev.me };
    for (const w of [...e.watchers]) w();
  });
  return list;
}

/** Your own status choice in a company, as last loaded here (undefined when nothing on screen has loaded it). */
export function myChoiceNow(tenant: string): PresenceList['me']['choice'] | undefined {
  return presenceLists.get(tenant.toLowerCase())?.list?.me.choice;
}

/** Your own choice and status changed here: every view follows at once. */
export function setMyPresence(tenant: string, myId: string, me: PresenceList['me'], shown: ChatPresence['status']): void {
  const e = presenceEntry(tenant.toLowerCase());
  if (!e.list) return;
  const was = e.list.people[myId];
  e.list = { ...e.list, me, people: { ...e.list.people, [myId]: { status: shown, custom: me.custom, phone: was?.phone ?? null } } };
  for (const w of [...e.watchers]) w();
}

// --- reactions ------------------------------------------------------------------------------------------

/** One person's emoji on (or off) a message's reactions: the same change twice changes nothing. */
export function applyReaction(groups: Reaction[], change: ReactionChange): Reaction[] {
  const group = groups.find((g) => g.emoji === change.emoji);
  const has = Boolean(group?.people.some((p) => p.id === change.person.id));
  if (change.on === has) return groups;
  if (change.on) return group ? groups.map((g) => (g === group ? { ...g, people: [...g.people, change.person] } : g)) : [...groups, { emoji: change.emoji, people: [change.person] }];
  return groups.map((g) => (g === group ? { ...g, people: g.people.filter((p) => p.id !== change.person.id) } : g)).filter((g) => g.people.length > 0);
}

/** A message as it now is, keeping reactions already on screen when the news is one reaction (they may be newer). */
export function updatedMessage(prev: MessageView, next: MessageView, change?: ReactionChange): MessageView {
  return change ? { ...next, reactions: applyReaction(prev.reactions, change) } : next;
}

// --- unread totals, per company, for the menu -------------------------------------------------------

/** As the server counts: someone else's message in the conversation (not a note, not a reply kept in its thread). */
export function countsAsUnread(m: MessageView, myId: string | undefined): boolean {
  if (m.kind === 'system' || (m.threadRootId && !m.alsoInChannel)) return false;
  return !(m.author.kind === 'person' && m.author.id === myId);
}

const totals = new Map<string, number>();
const watchers = new Set<() => void>();

/** The Messages page knows the exact total after loading: the menu follows it. */
export function setUnread(tenant: string, n: number): void {
  totals.set(tenant.toLowerCase(), n);
  for (const w of [...watchers]) w();
}

/** Where things stand before loading a total; `settle` keeps it only if nothing was read meanwhile. */
export function unreadMark(tenant: string): number {
  return reads.get(tenant.toLowerCase()) ?? 0;
}
export function settleUnread(tenant: string, mark: number, n: number): void {
  if (unreadMark(tenant) === mark) setUnread(tenant, n);
}

function loadTotal(key: string): void {
  const mark = unreadMark(key);
  api
    .messagesUnread(key)
    .then((r) => {
      settleUnread(key, mark, r.unread);
      if (unreadMark(key) === mark) setParts(key, { dms: r.dms, mentions: r.mentions });
    })
    .catch(() => undefined);
}

// --- the phone app's tabs: unread direct messages, and mentions of you ------------------------------

export interface UnreadPartsNow {
  dms: number;
  mentions: number;
}
const parts = new Map<string, UnreadPartsNow>();
const partWatchers = new Set<() => void>();
function setParts(key: string, p: UnreadPartsNow): void {
  parts.set(key, p);
  for (const w of [...partWatchers]) w();
}

/** Whether a message mentions this person. */
export const mentionsMe = (m: MessageView, myId: string | undefined): boolean => Boolean(myId && m.mentions.some((x) => x.id === myId));

/**
 * Unread direct messages and mentions in a company (the phone app's DMs and Activity tabs), kept up
 * to date live: a reading on another device sends the exact counts back.
 */
export function useUnreadParts(tenant: string, myId: string | undefined, on: boolean): UnreadPartsNow | undefined {
  const key = tenant.toLowerCase();
  const [p, setP] = useState<UnreadPartsNow | undefined>(() => parts.get(key));
  useEffect(() => {
    if (!on) return;
    let alive = true;
    const follow = () => alive && setP(parts.get(key));
    partWatchers.add(follow);
    follow();
    if (!parts.has(key)) loadTotal(key);
    return () => {
      alive = false;
      partWatchers.delete(follow);
    };
  }, [key, on]);
  const reload = useRef<number | undefined>(undefined);
  useMessagesLive((e) => {
    if (e.type === 'reset' || (e.tenant === key && e.type === 'conversation')) {
      window.clearTimeout(reload.current);
      reload.current = window.setTimeout(() => loadTotal(key), 400);
      return;
    }
    if (e.tenant !== key) return;
    const now = parts.get(key);
    if (e.type === 'read' && typeof e.dms === 'number' && typeof e.mentions === 'number') setParts(key, { dms: e.dms, mentions: e.mentions });
    else if (e.type === 'message' && e.message && now && !(e.message.author.kind === 'person' && e.message.author.id === myId) && e.message.kind !== 'system') {
      const dm = e.kind === 'dm' && countsAsUnread(e.message, myId);
      const mention = mentionsMe(e.message, myId);
      if (dm || mention) setParts(key, { dms: now.dms + (dm ? 1 : 0), mentions: now.mentions + (mention ? 1 : 0) });
    }
  }, on);
  return p;
}

/** Unread messages in a company, kept up to date live (undefined until known). */
export function useUnread(tenant: string, myId: string | undefined, on: boolean): number | undefined {
  const key = tenant.toLowerCase();
  const [n, setN] = useState<number | undefined>(() => totals.get(key));
  useEffect(() => {
    if (!on) return;
    let alive = true;
    const follow = () => alive && setN(totals.get(key));
    watchers.add(follow);
    loadTotal(key);
    return () => {
      alive = false;
      watchers.delete(follow);
    };
  }, [key, on]);
  const reload = useRef<number | undefined>(undefined);
  useMessagesLive((e) => {
    if (e.type === 'reset' || (e.tenant === key && e.type === 'conversation')) {
      window.clearTimeout(reload.current);
      reload.current = window.setTimeout(() => loadTotal(key), 400);
      return;
    }
    if (e.tenant !== key) return;
    if (e.type === 'read' && typeof e.unread === 'number') setUnread(key, e.unread);
    // Someone else's new message: one more (reading it sends the exact total back).
    else if (e.type === 'message' && e.message && countsAsUnread(e.message, myId)) setUnread(key, (totals.get(key) ?? 0) + 1);
  }, on);
  return n;
}
